// RigSentinel — Drilling Fluid Management (Phase RCE-3)
//
// DAILY MUD REPORT — operational document for the fluid family.
// Storage key: `rsc.fluidDocs.v1`. Scoped by `kind` + `wellId`.
//
// Mirrors the Daily Ops envelope: contextSnapshot frozen on first save,
// audit-trail invariant on updates, explicit re-pull only. The DMR's
// payload tracks the LIVE measured properties (the "current" leg of the
// mud-property tri-state) plus volumes / treatments / notes.

import {
  FLUID_KINDS, isKnownFluidKind, isFluidKindEnabled, CONTEXT_VERSION,
} from "./kinds";
import { emptyPropertyTable } from "./properties";
import { setOverride } from "@/lib/rce/overrides";
import { stampEngineeringMetadata } from "@/lib/rce/snapshot";
import { scoped } from "@/lib/accountScope";

// ---------------------------------------------------------------------------
// Resolver bootstrap (dependency injection — breaks circular import).
// `lib/rce/resolver.js` cannot be imported here because it transitively
// depends on `lib/mud/derivers.js` which imports back into this file.
// Bootstrap is performed once by `lib/rce/bootstrap.js`.
// ---------------------------------------------------------------------------
let _resolveFn = () => ({ value: "", confidence: "unknown", source: null, conflicts: [] });
export function _setResolver(fn) { if (typeof fn === "function") _resolveFn = fn; }export const FLUID_DOCS_SCHEMA_VERSION = 1;
export const FLUID_DOCS_STORAGE_KEY    = "rsc.fluidDocs.v1";

const _now   = () => new Date().toISOString();
const _genId = (kind) =>
  `fluiddoc_${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const _safeParse = (s, f) => { try { return JSON.parse(s) ?? f; } catch { return f; } };

const STATUSES = ["draft", "final", "archived"];

// Empty payload factory per kind.
const PAYLOAD_FACTORIES = {
  dailyMudReport: () => ({
    header:     { reportDate: "", preparedBy: "", mudEngineerCompany: "", reportNumber: "" },
    properties: emptyPropertyTable(),    // the LIVE measurements ("current" leg)
    volumes:    {},
    treatments: {},
    solidsControl: {},
    gainsLosses: {},
    notes:      "",
    recommendations: { nextTour: "" },
  }),
};

export function emptyFluidContextSnapshot() {
  return {
    frozen: false,
    snapshotId: "",
    contextVersion: CONTEXT_VERSION,
    capturedAt: null,
    project: { operator: "", client: "" },
    rig:     { name: "" },
    well:    { name: "", holeSection: "", currentMdFt: "", currentTvdFt: "" },
    mud:     {
      programId: "", fluidBasis: "",
      weightInPpg: "", plasticViscosityCp: "", yieldPointLbf100ft2: "",
      gelStrength10sLbf100ft2: "", gelStrength10minLbf100ft2: "",
      fluidLossMl30min: "", pH: "", sandContentPct: "",
      oilWaterRatio: "", electricalStabilityV: "", htHpFluidLossMl: "",
    },
    confidence: {},
    sessionContextIds: {
      projectId: "", rigId: "", wellId: "", mudProgramId: "",
    },
    sourceModules: [],
  };
}

export function emptyFluidDocument(kind = "dailyMudReport") {
  if (!isKnownFluidKind(kind))
    throw new Error(`Unknown Drilling Fluid Management kind: ${kind}`);
  const factory = PAYLOAD_FACTORIES[kind] || (() => ({}));
  return {
    id: "",
    kind,
    schemaVersion: FLUID_DOCS_SCHEMA_VERSION,
    contextVersion: CONTEXT_VERSION,
    wellId:        "",
    projectId:     "",
    rigId:         "",
    mudProgramId:  "",
    date:  "",
    status: "draft",
    contextSnapshot: emptyFluidContextSnapshot(),
    payload: factory(),
    notes: "",
    isActive: true,
    createdAt: null,
    updatedAt: null,
  };
}

export function validateFluidDocument(rec) {
  if (!rec || typeof rec !== "object")
    return { ok: false, errors: ["Fluid document is missing."] };
  const errs = [];
  if (!isKnownFluidKind(rec.kind))
    errs.push(`Unknown Drilling Fluid Management kind: ${rec.kind}.`);
  if (!isFluidKindEnabled(rec.kind))
    errs.push(`Kind "${rec.kind}" is reserved but not yet enabled in this phase.`);
  if (!STATUSES.includes(rec.status))
    errs.push(`Status must be one of: ${STATUSES.join(", ")}.`);
  return { ok: errs.length === 0, errors: errs };
}

// ---------------------------------------------------------------------------
// Context freeze / re-pull
// ---------------------------------------------------------------------------
function _assignDottedPath(target, dotted, value) {
  const parts = dotted.split(".");
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!cur[p] || typeof cur[p] !== "object") cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

export function buildFluidContextSnapshot(kind, ctx) {
  const k = FLUID_KINDS[kind];
  if (!k) throw new Error(`Unknown kind: ${kind}`);
  const snap = emptyFluidContextSnapshot();
  snap.frozen = true;
  snap.contextVersion = CONTEXT_VERSION;
  snap.capturedAt = new Date().toISOString();
  snap.sessionContextIds = {
    projectId:    ctx?.projectId    || "",
    rigId:        ctx?.rigId        || "",
    wellId:       ctx?.wellId       || "",
    mudProgramId: ctx?.mudProgramId || "",
  };
  for (const path of k.ownership.consumes) {
    const r = _resolveFn(path, ctx || {});
    const value = (r.confidence === "unknown" || r.confidence === "conflict")
      ? "" : r.value;
    _assignDottedPath(snap, path, value);
    snap.confidence[path] = r.confidence;
  }
  // RCE-4 — stamp the permanent Engineering Snapshot ID + Source Modules.
  stampEngineeringMetadata(snap);
  return snap;
}

export function freezeFluidContextSnapshot(rec, ctx) {
  const snap = buildFluidContextSnapshot(rec.kind, ctx);
  return { ...rec, contextSnapshot: snap, contextVersion: snap.contextVersion };
}

export function overrideFluidContextField(rec, path, value, reason = "") {
  const k = FLUID_KINDS[rec.kind];
  if (!k) throw new Error(`Unknown kind: ${rec.kind}`);
  if (!k.ownership.overrides.includes(path))
    throw new Error(`Document kind "${rec.kind}" cannot override field "${path}".`);
  if (!rec.id) throw new Error("Document must be persisted before overrides can be set.");
  return setOverride({
    scope: "document", scopeId: rec.id, fieldPath: path, value, reason,
  });
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------
function _readAll() { return _safeParse(scoped.getItem(FLUID_DOCS_STORAGE_KEY), []); }
function _writeAll(rows) { scoped.setItem(FLUID_DOCS_STORAGE_KEY, JSON.stringify(rows)); }

function _validateOrThrow(rec) {
  const v = validateFluidDocument(rec);
  if (!v.ok) {
    const e = new Error("Fluid document failed validation.");
    e.validationErrors = v.errors;
    throw e;
  }
}

export function createFluidDocument(kind, payloadPatch = {}, ctx = null) {
  if (!isFluidKindEnabled(kind))
    throw new Error(`Kind "${kind}" is registered but not enabled in this phase.`);
  let base = emptyFluidDocument(kind);
  base = {
    ...base,
    payload: { ...base.payload, ...(payloadPatch || {}) },
    id: _genId(kind),
    createdAt: _now(),
    updatedAt: _now(),
    wellId:       ctx?.wellId       || "",
    projectId:    ctx?.projectId    || "",
    rigId:        ctx?.rigId        || "",
    mudProgramId: ctx?.mudProgramId || "",
  };
  if (ctx) base = freezeFluidContextSnapshot(base, ctx);
  _validateOrThrow(base);
  const all = _readAll();
  all.push(base);
  _writeAll(all);
  return base;
}

export function getFluidDocument(id) {
  return _readAll().find((r) => r.id === id) || null;
}

export function listFluidDocuments({ kind, wellId, includeInactive = false } = {}) {
  let rows = _readAll();
  if (kind)   rows = rows.filter((r) => r.kind === kind);
  if (wellId) rows = rows.filter((r) => r.wellId === wellId);
  if (!includeInactive) rows = rows.filter((r) => r.isActive !== false);
  return rows
    .slice()
    .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
}

export function updateFluidDocument(id, patch) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Fluid document ${id} not found.`);
  const prev = all[i];
  const next = {
    ...prev,
    ...patch,
    id: prev.id,
    kind: prev.kind,
    createdAt: prev.createdAt,
    contextSnapshot: prev.contextSnapshot,    // NEVER auto-clobber
    contextVersion: prev.contextVersion,
    payload: { ...prev.payload, ...(patch?.payload || {}) },
  };
  next.updatedAt = _now();
  _validateOrThrow(next);
  all[i] = next;
  _writeAll(all);
  return next;
}

export function rePullFluidContextSnapshot(id, ctx) {
  const all = _readAll();
  const i = all.findIndex((r) => r.id === id);
  if (i < 0) throw new Error(`Fluid document ${id} not found.`);
  const prev = all[i];
  if (prev.status === "final" || prev.status === "archived")
    throw new Error(`Cannot re-pull context: document is ${prev.status}.`);
  const refreshed = freezeFluidContextSnapshot(prev, ctx || {});
  refreshed.updatedAt = _now();
  _validateOrThrow(refreshed);
  all[i] = refreshed;
  _writeAll(all);
  return refreshed;
}

export function deleteFluidDocument(id) {
  const all = _readAll();
  const next = all.filter((r) => r.id !== id);
  _writeAll(next);
  return all.length - next.length;
}

export function _clearAllFluidDocs() {
  scoped.removeItem(FLUID_DOCS_STORAGE_KEY);
}
